/** Normalized player intent for one simulation tick. Pure data — no DOM/Three. */

export type ButtonFlags = {
  firePrimary: boolean;
  fireSecondary: boolean;
  fireOrdnance: boolean;
  boost: boolean;
  interact: boolean;
  pause: boolean;
  restart: boolean;
};

export type PlayerCommand = {
  /** Monotonic sequence from the input producer. */
  seq: number;
  /** Simulation tick this command is intended for (filled by host). */
  tick: number;
  /** Camera-relative east/north move axes in [-1, 1]. */
  moveX: number;
  moveY: number;
  /** Aim direction on the local tangent plane in [-1, 1]; zero = keep facing. */
  aimX: number;
  aimY: number;
  /** -1 = none; 0–2 picks a level-up skill offer. */
  skillPick: number;
  buttons: ButtonFlags;
};

export type CommandBuffer = {
  commands: PlayerCommand[];
};

export function createEmptyButtons(): ButtonFlags {
  return {
    firePrimary: false,
    fireSecondary: false,
    fireOrdnance: false,
    boost: false,
    interact: false,
    pause: false,
    restart: false,
  };
}

export function createNeutralCommand(seq = 0, tick = 0): PlayerCommand {
  return {
    seq,
    tick,
    moveX: 0,
    moveY: 0,
    aimX: 0,
    aimY: 0,
    skillPick: -1,
    buttons: createEmptyButtons(),
  };
}

export function clampAxis(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(1, Math.max(-1, value));
}

export function normalizeCommand(command: PlayerCommand): PlayerCommand {
  return {
    ...command,
    moveX: clampAxis(command.moveX),
    moveY: clampAxis(command.moveY),
    aimX: clampAxis(command.aimX),
    aimY: clampAxis(command.aimY),
    skillPick: command.skillPick,
    buttons: { ...command.buttons },
  };
}
